import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import {
    Button,
    Chip,
    List,
    ListItem,
    ListItemText,
    Popover,
    Typography
} from '@mui/material'
import { useState } from 'react'
import useAuth from '@/hooks/useAuth'
import useTerminology from '@/hooks/useTerminology'
import { isLocationScoped, scopedLocations } from '@/lib/locationScope'

/**
 * The header for a location member (site manager / tenant), in place of the org
 * switcher: the one granted Location's name, or "{n} {houses}" with a popover
 * listing them. With no grants it says so and names who can grant access
 * (location-scoped-member-role).
 */
export function grantedLocations(user) {
    const locations = scopedLocations(user)
    const granted = locations.filter((location) => location.granted)
    return granted.length ? granted : locations
}

export default function LocationScopeBadge() {
    const { user } = useAuth() ?? {}
    const { t } = useTerminology()
    const [anchor, setAnchor] = useState(null)

    if (!isLocationScoped(user)) return null
    const granted = grantedLocations(user)

    if (granted.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" noWrap>
                {`You don't have access to any ${t('houses').toLowerCase()} yet. Ask ${t('orgAdminRoleName')}.`}
            </Typography>
        )
    }

    if (granted.length === 1) {
        return (
            <Chip
                icon={<PlaceOutlinedIcon />}
                label={granted[0].name}
                variant="outlined"
                data-testid="location-scope-badge"
            />
        )
    }

    return (
        <>
            <Button
                size="small"
                variant="outlined"
                startIcon={<PlaceOutlinedIcon />}
                onClick={(event) => setAnchor(event.currentTarget)}
                aria-haspopup="true"
            >
                {`${granted.length} ${t('houses')}`}
            </Button>
            <Popover
                open={Boolean(anchor)}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <List dense>
                    {granted.map((location) => (
                        <ListItem key={location.id}>
                            <ListItemText primary={location.name} />
                        </ListItem>
                    ))}
                </List>
            </Popover>
        </>
    )
}
